import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";

export default function WaitlistSection() {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // EmailJS account credentials
  const EMAILJS_SERVICE_ID = "service_hdov8s6";
  const EMAILJS_TEMPLATE_ID = "template_74kx29x";
  const EMAILJS_PUBLIC_KEY = "y459mQFz3zIKOmCR8";

  // Map form radio values to EmailJS template userType values
  const userTypeMap: Record<string, string> = {
    customer: "customer",
    vendor: "seller",
    rider: "Delivery person",
  };

  const handleSubmitClick = async () => {
    const form = formRef.current;
    if (!form) return;

    const formData = new FormData(form);

    const fullName = (formData.get("name") as string).trim();
    const phoneNumber = (formData.get("phone") as string).trim();
    const email = (formData.get("email") as string).trim();
    const neighborhood = (formData.get("neighborhood") as string).trim();
    const status = (formData.get("status") as string).trim();

    console.log("[Mayouk] Form extracted:", { fullName, phoneNumber, email, neighborhood, status });

    if (!fullName || !phoneNumber || !neighborhood || !status) {
      setError("Veuillez remplir tous les champs.");
      return;
    }

    setError("");
    setSubmitting(true);

    const templateParams = {
      fullName,
      phoneNumber,
      email: email || "Non renseigne",
      neighborhood,
      userType: userTypeMap[status] || status,
    };

    console.log("[Mayouk] Sending EmailJS payload:", templateParams);

    try {
      const result = await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      console.log("[Mayouk] EmailJS result:", { status: result.status, text: result.text });

      if (result && result.status === 200) {
        setSubmitted(true);
      } else {
        setError("Le serveur a repondu de maniere inattendue. Veuillez reessayer.");
      }
    } catch (err: any) {
      console.error("[Mayouk] EmailJS send FAILED:", err);
      if (err?.text)   console.error("[Mayouk] Server error text:", err.text);
      if (err?.status) console.error("[Mayouk] HTTP status:", err.status);
      if (err?.name)   console.error("[Mayouk] Error name:", err.name);
      setError("Une erreur reseau est survenue. Verifiez votre connexion et reessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="waitlist" className="relative w-full py-20 md:py-28 bg-background-50">
      <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold uppercase tracking-wider mb-5">
            {t("waitlist.title")}
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
            {t("waitlist.title")}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 max-w-xl mx-auto">
            {t("waitlist.subtitle")}
          </p>
          <div className="w-20 h-1 bg-primary-500 rounded-full mx-auto mt-5"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Form */}
          <div>
            {submitted ? (
              <div className="bg-background-100 rounded-2xl border border-background-200 p-10 md:p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-6">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-check-line text-primary-600 text-3xl"></i>
                  </div>
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-3">
                  Merci !
                </h3>
                <p className="text-sm text-foreground-600">
                  {t("waitlist.success")}
                </p>
              </div>
            ) : (
              <form
                id="waitlist-form"
                ref={formRef}
                className="bg-background-100 rounded-2xl border border-background-200 p-7 md:p-9 space-y-5"
              >
                {error && (
                  <div className="bg-accent-100 text-accent-800 text-sm rounded-xl px-4 py-3">
                    {error}
                  </div>
                )}
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-2">
                    {t("waitlist.full_name")}
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Ex: Jean Dupont"
                    className="w-full px-4 py-3.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-2">
                    {t("waitlist.phone")}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+237 6XX XX XX XX"
                    className="w-full px-4 py-3.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="exemple@email.com"
                    className="w-full px-4 py-3.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-2">
                    {t("waitlist.neighborhood")}
                  </label>
                  <input
                    type="text"
                    name="neighborhood"
                    placeholder="Ex: Akwa, Bonapriso..."
                    className="w-full px-4 py-3.5 rounded-xl border border-background-300 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground-800 mb-2">
                    {t("waitlist.status")}
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { value: "customer", label: t("waitlist.status_customer"), icon: "ri-user-line" },
                      { value: "vendor", label: t("waitlist.status_vendor"), icon: "ri-store-2-line" },
                      { value: "rider", label: t("waitlist.status_rider"), icon: "ri-motorbike-line" },
                    ].map((opt) => (
                      <label key={opt.value} className="relative cursor-pointer">
                        <input
                          type="radio"
                          name="status"
                          value={opt.value}
                          className="peer sr-only"
                          required
                        />
                        <div className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl border-2 border-background-300 bg-background-50 text-center peer-checked:border-primary-500 peer-checked:bg-primary-50 transition-all hover:border-background-400">
                          <div className="w-6 h-6 flex items-center justify-center">
                            <i className={`${opt.icon} text-foreground-500 peer-checked:text-primary-600 text-lg`}></i>
                          </div>
                          <span className="text-xs font-medium text-foreground-600 peer-checked:text-primary-700 whitespace-nowrap">
                            {opt.label}
                          </span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSubmitClick}
                  disabled={submitting}
                  className="w-full py-4 bg-primary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all hover:scale-[1.02] shadow-lg shadow-primary-500/20 whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                >
                  {submitting ? "Envoi en cours..." : t("waitlist.submit")}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="font-heading text-xl font-semibold text-foreground-950 mb-6">
              {t("waitlist.contact_title")}
            </h3>
            <div className="space-y-4 mb-8">
              {[
                { icon: "ri-phone-line", color: "bg-primary-100 text-primary-600", label: t("waitlist.phone_label"), value: t("waitlist.phone_value"), href: "tel:+237681172545" },
                { icon: "ri-whatsapp-line", color: "bg-accent-100 text-accent-600", label: t("waitlist.whatsapp_label"), value: t("waitlist.whatsapp_value"), href: "https://wa.me/237681172545" },
                { icon: "ri-mail-line", color: "bg-secondary-100 text-secondary-600", label: t("waitlist.email_label"), value: t("waitlist.email_value"), href: "mailto:info@mayouk.com" },
                { icon: "ri-map-pin-line", color: "bg-primary-100 text-primary-600", label: t("waitlist.location_label"), value: t("waitlist.location_value"), href: null },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 bg-background-100 rounded-xl p-4 border border-background-200 hover:border-primary-200 transition-all">
                  <div className={`w-11 h-11 rounded-xl ${item.color} flex items-center justify-center flex-shrink-0`}>
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className={`${item.icon}`}></i>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground-500 mb-0.5">{item.label}</p>
                    {item.href ? (
                      <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel={item.href.startsWith("http") ? "noopener noreferrer nofollow" : undefined} className="text-sm text-foreground-800 hover:text-primary-600 transition-colors truncate block">
                        {item.value}
                      </a>
                    ) : (
                      <p className="text-sm text-foreground-800">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden border border-background-200">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15919.3!2d9.7679!3d4.0511!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x10610d7a1e8b3b0f%3A0x7a3e3e3e3e3e3e3e!2sAkwa%2C%20Douala%2C%20Cameroon!5e0!3m2!1sen!2scm!4v1700000000000!5m2!1sen!2scm"
                width="100%"
                height="280"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Mayouk - Akwa, Douala"
                className="w-full"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
